import { useState } from 'react';

import {
  ChevronsUpDown,
  Loader2,
  Package,
  Plus,
  Send,
  Upload,
} from 'lucide-react';

import { useGetSandbox } from '@/api/getSandbox';
import { useGetSandboxes } from '@/api/getSandboxes';
import { AppTooltip } from '@/components/appTooltip';
import { usePopup } from '@/components/confirmation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StateBadge } from '@/components/ui/state-badge';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useGame } from '@/hooks/useGame';
import { useSandboxSwitch } from '@/hooks/useGameSandboxSwitch';
import { toaster } from '@/lib/toaster';
import { type SandboxPublishStatus } from '@/models/sandbox';
import { useSandboxContext } from '@/providers';

import { CreateSandboxModal } from './createSandboxModal';
import { SnapshotPublishSummaryDialog } from './snapshotPublishSummaryDialog';
import { useExportSandbox } from '../api/exportSandbox';
import {
  type PublishSandboxResponse,
  usePublishSandbox,
} from '../api/publishSandbox';

/**
 * Builds a text string for the tooltip showing pending changes counts.
 * Only includes counts that are greater than 0.
 */
const buildPendingCountsTooltip = (status: SandboxPublishStatus): string => {
  const parts: string[] = [];
  if (status.dirty_items_count > 0) {
    parts.push(`${status.dirty_items_count} dirty item(s)`);
  }
  if (status.ready_snapshots_count > 0) {
    parts.push(`${status.ready_snapshots_count} ready snapshot(s)`);
  }
  if (status.ready_gifts_count > 0) {
    parts.push(`${status.ready_gifts_count} ready gift(s)`);
  }
  return parts.join(', ');
};

/**
 * Builds a text string for the publish confirmation showing what will be published.
 * Only includes counts that are greater than 0.
 */
const buildPublishConfirmationText = (status: SandboxPublishStatus): string => {
  const parts: string[] = [];
  if (status.dirty_items_count > 0) {
    parts.push(`${status.dirty_items_count} item(s)`);
  }
  if (status.ready_snapshots_count > 0) {
    parts.push(`${status.ready_snapshots_count} snapshot(s)`);
  }
  if (status.ready_gifts_count > 0) {
    parts.push(`${status.ready_gifts_count} gift(s)`);
  }
  return parts.join(' and ');
};

interface SandboxBreadcrumbDropdownProps {
  sandboxCode: string;
}

/**
 * Sandbox Breadcrumb Dropdown Component
 * Displays sandbox selector with state badge and actions in the breadcrumb
 */
export const SandboxBreadcrumbDropdown: React.FC<
  SandboxBreadcrumbDropdownProps
> = ({ sandboxCode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [publishResult, setPublishResult] =
    useState<PublishSandboxResponse | null>(null);

  const { selectedSandbox, gameCode } = useSandboxContext();
  const game = useGame();
  const { handleInfo } = useErrorHandler();
  const { showPopup } = usePopup();

  const { switchSandbox } = useSandboxSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const { data: sandboxes } = useGetSandboxes({ game_code: game.game_code });

  // Find the current sandbox to get its ID for actions
  const currentSandbox = sandboxes?.sandboxes.find(
    (sandbox) => sandbox.code === selectedSandbox,
  );

  // Fetch single sandbox for publish_status (enables query invalidation to refresh breadcrumb)
  const { data: sandboxDetail } = useGetSandbox(
    { sandboxId: currentSandbox?.id ?? '' },
    { enabled: !!currentSandbox?.id },
  );

  const publishStatus = sandboxDetail?.publish_status ?? null;
  const canPublish = publishStatus?.has_pending === true;

  const exportMutation = useExportSandbox({
    mutationConfig: {
      onSuccess: () => {
        setIsOpen(false);
      },
    },
  });

  const publishMutation = usePublishSandbox();

  const isLoading = exportMutation.isPending || publishMutation.isPending;

  const handleSandboxChange = (newSandbox: string) => {
    switchSandbox(gameCode, selectedSandbox, newSandbox);
  };

  const handleCreate = () => {
    setIsCreateModalOpen(true);
    setIsOpen(false);
  };

  const handleExport = () => {
    if (!currentSandbox) {
      handleInfo('No sandbox selected');
      return;
    }

    exportMutation.mutate({ sandboxId: currentSandbox.id });
  };

  const handlePublish = async () => {
    if (!currentSandbox) {
      handleInfo('No sandbox selected');
      return;
    }

    setIsOpen(false);

    const confirmed = await showPopup({
      title: 'Confirm Publish',
      content: (
        <div className="space-y-2">
          <p>
            Are you sure you want to publish sandbox{' '}
            <strong>{currentSandbox.name}</strong>?
          </p>
          {publishStatus && publishStatus.has_pending && (
            <p className="text-sm text-muted-foreground">
              This will publish {buildPublishConfirmationText(publishStatus)}.
            </p>
          )}
        </div>
      ),
      confirmText: 'Publish',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const result = await publishMutation.mutateAsync({
          sandboxId: currentSandbox.id,
          gameCode: game.game_code,
        });
        toaster.success('Sandbox published successfully');
        setPublishResult(result);
      },
    });

    if (!confirmed) {
      return;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Package size={16} />
      {publishStatus?.has_pending && (
        <AppTooltip title={buildPendingCountsTooltip(publishStatus)}>
          <div>
            <StateBadge state="dirty" />
          </div>
        </AppTooltip>
      )}

      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={isLoading}>
            <span className="truncate max-w-48">
              {currentSandbox?.name ?? sandboxCode}
            </span>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronsUpDown />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-xs">
          <DropdownMenuRadioGroup
            value={selectedSandbox}
            onValueChange={handleSandboxChange}
            className="max-h-48 overflow-y-auto"
          >
            {sandboxes?.sandboxes.map((sandbox) => (
              <DropdownMenuRadioItem key={sandbox.code} value={sandbox.code}>
                <div className="flex w-full items-center justify-between gap-4">
                  <span>{sandbox.name}</span>
                </div>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            <span>Create Sandbox</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleExport}
            disabled={isLoading || !currentSandbox}
          >
            <Upload className="mr-2 h-4 w-4" />
            <span>Export Sandbox</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handlePublish}
            disabled={isLoading || !currentSandbox || !canPublish}
          >
            <Send className="mr-2 h-4 w-4" />
            <div className="flex flex-col">
              <span>Publish Sandbox</span>
              {!canPublish && (
                <span className="text-xs text-muted-foreground">
                  No pending changes
                </span>
              )}
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateSandboxModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
      {publishResult && (
        <SnapshotPublishSummaryDialog
          open={!!publishResult}
          onOpenChange={(open) => {
            if (!open) setPublishResult(null);
          }}
          response={publishResult}
        />
      )}
    </div>
  );
};
